/* 
Renders the correct action component based on Livepeer state and selected Tab
*/
import { List, Stack, Text, Group, Tooltip, Tabs } from "@mantine/core";
import "./styles.css";
import ApproveLPT from "./actions/ApproveLPT";
import Newstake from "./actions/NewStake";
import Unbond from "./actions/Unbond";
import WithdrawFees from "./actions/WithdrawFees";
import WithdrawStake from "./WithdrawStake";
import MoveStake from "./actions/MoveStake";
import IncreaseStake from "./actions/IncreaseStake";
import SetOrchestrator from "./components/SetOrchestrator";
import {
  IconTrendingUp,
  IconTrendingDown,
  IconCash,
  IconStepInto,
  IconChevronsRight,
  IconEdit,
} from "@tabler/icons";
import { useState } from "react";
import { ethers } from "ethers";

export default function StakeViewSelector(props) {
  // By default open the move/increase stake tab
  const [activeTab, setActiveTab] = useState("bond");
  // If we are not staked at the current Orchestrator, replace Increase stake with Move stake
  const isMoveStake =
    props.bondedWith.toLowerCase() != props.orchestratorAddr.toLowerCase()
      ? true
      : false;
  // In case of error, we want to notify the user they can go here to use the official explorer to stake to us
  const routeChange = () => {
    location.href =
      "https://explorer.livepeer.org/accounts/" +
      props.orchestratorAddr +
      "/orchestrating";
  };

  // If not connected through Metamask, show a quick overview/tutorial
  if (props.isSyncing && props.allowance == 0) {
    return (
      <Text size="xl" fw={700}>
        Retrieving Delegator info...
      </Text>
    );
  } else if (!props.connectedWallet || props.connectedWallet == "") {
    return (
      <Stack spacing={12} sx={{ height: "100%" }}>
        <Text size="xl" fw={700}>
          Staking to Video Miner goes through three easy steps:
        </Text>
        <List size="sm" withPadding>
          <List.Item>
            <Tooltip
              label={"Connect to your Ethereum account through Metamask"}
            >
              <Text
                className="textLink"
                size="lg"
                fw={700}
                style={{ cursor: "help" }}
              >
                Connect your wallet
              </Text>
            </Tooltip>
          </List.Item>
          <List.Item>
            <Tooltip
              label={
                "Approve Livepeer's BondingManager smart contract to be able to stake, unstake or withdraw"
              }
            >
              <Text
                className="textLink"
                size="lg"
                fw={700}
                style={{ cursor: "help" }}
              >
                Approve the LPT token
              </Text>
            </Tooltip>
          </List.Item>
          <List.Item>
            <Tooltip
              label={
                "Bond your LPT to video miner and start earning ETH and LPT"
              }
            >
              <Text
                className="textLink"
                size="lg"
                fw={700}
                style={{ cursor: "help" }}
              >
                Bond LPT to Video Miner
              </Text>
            </Tooltip>
          </List.Item>
        </List>
        <Text size="xl" fw={700}>
          After this you can unbond your stake at any time. Keep in mind that
          there is an unbonding period of 7 days built into the Livepeer
          protocol. When unbonding you can only withdraw your LPT tokens after
          this period of 7 days
        </Text>
        <Text size="xl" fw={700} onClick={routeChange} className="textLink">
          If you do not use MetaMask or want to stake through Livepeer's
          official Explorer, click here to be redirected
        </Text>
      </Stack>
    );
    // If we do not have an allowance, the user needs to approve LPT first
  } else if (props.allowance == 0) {
    return (
      <ApproveLPT
        connectedWallet={props.connectedWallet}
        refreshWallet={props.refreshWallet}
        setErr={props.setErr}
      />
    );
    // If we are not bonded yet, show a special box for new Delegators
  } else if (
    props.bondedWith == "" ||
    props.bondedWith == ethers.constants.AddressZero
  ) {
    return (
      <Tabs
        color="#86c232"
        variant="pills"
        orientation="vertical"
        defaultValue="bond"
        value={activeTab}
        onTabChange={setActiveTab}
        sx={{ height: "100%", width: "100%" }}
      >
        <Tabs.List>
          <Tabs.Tab
            value="bond"
            icon={<IconChevronsRight size={32} color={"#86c232"} />}
          >
            Stake
          </Tabs.Tab>
          <Tabs.Tab
            value="SetOrchestrator"
            icon={<IconEdit size={32} color={"#86c232"} />}
          >
            Change Orchestrator
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="bond" pl="xs">
          <Newstake
            setErr={props.setErr}
            getHints={props.getHints}
            allowance={props.allowance}
            balance={props.LPTbalance}
            inputValue={props.inputValue}
            setInput={props.setInput}
            orchestratorAddr={props.orchestratorAddr}
            cachedENS={props.cachedENS}
            refreshWallet={props.refreshWallet}
          />
        </Tabs.Panel>
        <Tabs.Panel value="SetOrchestrator" pl="xs">
          <SetOrchestrator
            orchestratorAddr={props.orchestratorAddr}
            setOrchestrator={props.setOrchestrator}
            transcoderTable={props.transcoderTable}
          />
        </Tabs.Panel>
      </Tabs>
    );
    // Otherwise we can show the regular menu of Increase/Move stake, Decrease stake,
    // withdraw LPT and withdraw Fees
  } else {
    return (
      <Tabs
        color="#86c232"
        variant="pills"
        orientation="vertical"
        defaultValue="bond"
        value={activeTab}
        onTabChange={setActiveTab}
        sx={{ height: "100%", width: "100%" }}
      >
        <Tabs.List>
          {isMoveStake ? (
            <Tabs.Tab
              value="bond"
              icon={<IconChevronsRight size={32} color={"#86c232"} />}
            >
              Move Stake
            </Tabs.Tab>
          ) : (
            <Tabs.Tab
              value="bond"
              icon={<IconTrendingUp size={32} color={"#86c232"} />}
            >
              Increase Stake
            </Tabs.Tab>
          )}
          <Tabs.Tab
            value="unbond"
            icon={<IconTrendingDown size={32} color={"#86c232"} />}
          >
            Decrease Stake
          </Tabs.Tab>
          <Tabs.Tab
            value="WithdrawLPT"
            icon={<IconStepInto size={32} color={"#86c232"} />}
          >
            Withdraw Stake
          </Tabs.Tab>
          <Tabs.Tab
            value="WithdrawFees"
            icon={<IconCash size={32} color={"#86c232"} />}
          >
            Withdraw Fees
          </Tabs.Tab>
          <Tabs.Tab
            value="SetOrchestrator"
            icon={<IconEdit size={32} color={"#86c232"} />}
          >
            Change Orchestrator
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="bond" pl="xs">
          {isMoveStake ? (
            <MoveStake
              setErr={props.setErr}
              getHints={props.getHints}
              orchestratorAddr={props.orchestratorAddr}
              bondedLPT={props.bondedLPT}
              bondedWith={props.bondedWith}
              refreshWallet={props.refreshWallet}
              cachedENS={props.cachedENS}
            />
          ) : (
            <IncreaseStake
              setErr={props.setErr}
              getHints={props.getHints}
              orchestratorAddr={props.orchestratorAddr}
              bondedLPT={props.bondedLPT}
              bondedWith={props.bondedWith}
              allowance={props.allowance}
              balance={props.LPTbalance}
              inputValue={props.inputValue}
              setInput={props.setInput}
              refreshWallet={props.refreshWallet}
              cachedENS={props.cachedENS}
            />
          )}
        </Tabs.Panel>
        <Tabs.Panel value="unbond" pl="xs">
          <Unbond
            setErr={props.setErr}
            getHints={props.getHints}
            orchestratorAddr={props.orchestratorAddr}
            balance={props.bondedLPT}
            inputValue={props.inputValue}
            setInput={props.setInput}
            connectedWallet={props.connectedWallet}
            refreshWallet={props.refreshWallet}
            delegatorState={props.delegatorState}
            bondedWith={props.bondedWith}
          />
        </Tabs.Panel>
        <Tabs.Panel value="WithdrawFees" pl="xs">
          <WithdrawFees
            connectedWallet={props.connectedWallet}
            setErr={props.setErr}
            pendingEth={props.pendingEth}
            refreshWallet={props.refreshWallet}
          />
        </Tabs.Panel>
        <Tabs.Panel value="WithdrawLPT" pl="xs">
          <WithdrawStake
            connectedWallet={props.connectedWallet}
            setErr={props.setErr}
            thisRound={props.thisRound}
            locks={props.locks}
            refreshWallet={props.refreshWallet}
          />
        </Tabs.Panel>
        <Tabs.Panel value="SetOrchestrator" pl="xs">
          <SetOrchestrator
            orchestratorAddr={props.orchestratorAddr}
            setOrchestrator={props.setOrchestrator}
            transcoderTable={props.transcoderTable}
          />
        </Tabs.Panel>
      </Tabs>
    );
  }
}
